import React from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import { firestore } from './firebase';

const SUBJECT_FILTER = 'Neue Unterschrift: Warum lohnt es sich?';

const OpenLetterReport: React.FC = () => {
  const query = React.useMemo(
    () => firestore.collection('mail').where('message.subject', '==', SUBJECT_FILTER),
    []
  );
  const [snapshot, loading, error] = useCollection(query);
  const entries = snapshot?.docs ?? [];

  const extractFields = React.useCallback((html: string) => {
    const result = {
      name: '—',
      email: '—',
      occupation: '—',
      uuid: '—',
    };

    if (typeof document === 'undefined') {
      return result;
    }

    const template = document.createElement('template');
    template.innerHTML = html;

    const rows = Array.from(template.content.querySelectorAll('tr'));
    rows.forEach((row) => {
      const cells = row.querySelectorAll('td');
      if (cells.length < 2) {
        return;
      }

      const key = cells[0].textContent?.trim().toLowerCase();
      const value = cells[1].textContent?.trim() ?? '—';

      switch (key) {
        case 'name':
          result.name = value;
          break;
        case 'e-mail':
        case 'email':
          result.email = value;
          break;
        case 'beruf':
        case 'tätigkeit / beruf':
          result.occupation = value;
          break;
        case 'open-letter-uuid':
          result.uuid = value;
          break;
        default:
          break;
      }
    });

    return result;
  }, []);

  const parsedEntries = React.useMemo(
    () =>
      entries.map((document) => {
        const data = document.data();
        const htmlContent =
          typeof data?.message?.html === 'string' ? data.message.html : '';
        const fields = extractFields(htmlContent);

        return {
          id: document.id,
          ...fields,
        };
      }),
    [entries, extractFields]
  );

  return (
    <div className="open-letter-report-layout">
      <div className="open-letter-report-card">
        <h1 className="open-letter-report-title">Open Letter Signatures Report</h1>

        {loading && <div className="open-letter-report-status">Lädt…</div>}
        {error && (
          <div className="open-letter-report-status open-letter-report-status-error">
            Die Daten konnten nicht geladen werden.
          </div>
        )}

        {!loading && !error && entries.length === 0 && (
          <div className="open-letter-report-status">Keine Einträge gefunden.</div>
        )}

        {!loading && !error && parsedEntries.length > 0 && (
          <div className="open-letter-report-table-wrapper">
            <table className="open-letter-report-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>E-Mail</th>
                  <th>Beruf</th>
                  <th>Open-Letter-UUID</th>
                  <th>Dokumenten-ID</th>
                </tr>
              </thead>
              <tbody>
                {parsedEntries.map((entry, index) => (
                  <tr key={entry.id}>
                    <td>{index + 1}</td>
                    <td>{entry.name}</td>
                    <td>{entry.email}</td>
                    <td>{entry.occupation}</td>
                    <td>{entry.uuid}</td>
                    <td className="open-letter-report-id">{entry.id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .open-letter-report-layout {
          min-height: 100vh;
          padding: 32px 16px;
          background: #f7fafc;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          width: 100%;
          overflow-y: auto;
        }

        .open-letter-report-card {
          width: 100%;
          max-width: 900px;
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 12px 30px rgba(18, 38, 63, 0.12);
          padding: 32px;
          max-height: calc(100vh - 64px);
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .open-letter-report-title {
          font-size: 2rem;
          margin-bottom: 24px;
        }

        .open-letter-report-status {
          padding: 16px;
          border-radius: 12px;
          background: #edf2f7;
          color: #2d3748;
        }

        .open-letter-report-status-error {
          background: #fff5f5;
          color: #c53030;
        }

        .open-letter-report-table-wrapper {
          overflow-x: auto;
        }

        .open-letter-report-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 680px;
        }

        .open-letter-report-table th,
        .open-letter-report-table td {
          border: 1px solid #e2e8f0;
          padding: 12px;
          text-align: left;
          vertical-align: top;
        }

        .open-letter-report-table th {
          background: #f1f5f9;
          color: #1a202c;
          font-weight: 600;
        }

        .open-letter-report-table tbody tr:nth-child(odd) {
          background: #f8fafc;
        }

        .open-letter-report-id {
          font-family: monospace;
          font-size: 0.85rem;
          word-break: break-all;
        }

        @media (max-width: 640px) {
          .open-letter-report-layout {
            padding: 24px 12px;
          }

          .open-letter-report-card {
            padding: 24px;
            max-height: none;
          }

          .open-letter-report-title {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default OpenLetterReport;
